import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { reaction } from 'mobx';
import { RouterView } from 'mobx-state-router';
import { makeStyles } from '@mui/styles';
import * as Sentry from '@sentry/react';
import { useStore } from 'logic';
import { getTheme } from 'theme';
import { ApplicationBar } from 'ui/ApplicationBar';
import { AppSnackbar } from 'ui/AppSnackbar';
import { RouteNames } from 'logic/routes';
import { isWholeViewEnabled } from 'logic/flags';
import { CustomKbar } from 'ui/CustomKbar';
import { ConfirmProvider } from 'material-ui-confirm';
import { BrandedLoading } from 'ui/BrandedLoading';
import { shouldBypassAppLoaderForProjectSearch } from 'logic/project-search-loading';
import { useAppStartupTracingFinish } from 'logic/startupTracing';

const HomeView = lazy(() => import('views/HomeView').then((mod) => ({ default: mod.HomeView })));
const ProjectsView = lazy(() =>
  import('views/ProjectsView').then((mod) => ({ default: mod.ProjectsView })),
);
const ImportCSVView = lazy(() =>
  import('views/ImportCSVView').then((mod) => ({ default: mod.ImportCSVView })),
);
const ImportListView = lazy(() =>
  import('views/ImportListView').then((mod) => ({ default: mod.ImportListView })),
);
const ImportBlankSpreadsheetView = lazy(() =>
  import('views/ImportBlankSpreadsheetView').then((mod) => ({
    default: mod.ImportBlankSpreadsheetView,
  })),
);
const ImportImageView = lazy(() =>
  import('views/ImportImageView').then((mod) => ({ default: mod.ImportImageView })),
);
const ImportPDFView = lazy(() =>
  import('views/ImportPDFView').then((mod) => ({ default: mod.ImportPDFView })),
);
const OnboardingView = lazy(() =>
  import('views/OnboardingView').then((mod) => ({ default: mod.OnboardingView })),
);
const ActiveSessionView = lazy(() =>
  import('views/ActiveSessionView').then((mod) => ({ default: mod.ActiveSessionView })),
);
const OrganizationAdmin = lazy(() =>
  import('views/OrganizationAdmin').then((mod) => ({ default: mod.OrganizationAdmin })),
);
const CQAdmin = lazy(() => import('views/CQAdmin').then((mod) => ({ default: mod.CQAdmin })));
const OrganizationDetailsView = lazy(() =>
  import('views/OrganizationDetailsView').then((mod) => ({
    default: mod.OrganizationDetailsView,
  })),
);
const SessionShareView = lazy(() =>
  import('views/SessionShareView').then((mod) => ({ default: mod.SessionShareView })),
);
const ClaimInvitationView = lazy(() =>
  import('views/ClaimInvitationView').then((mod) => ({ default: mod.ClaimInvitationView })),
);
const ApproveOwnershipTransferView = lazy(() =>
  import('views/ApproveOwnershipTransferView').then((mod) => ({
    default: mod.ApproveOwnershipTransferView,
  })),
);
const TourView = lazy(() => import('views/TourView').then((mod) => ({ default: mod.TourView })));
const AccountAccessBlocked = lazy(() =>
  import('ui/AccountAccessBlocked').then((mod) => ({ default: mod.AccountAccessBlocked })),
);
const OrganizationSetupView = lazy(() =>
  import('views/OrganizationSetupView').then((mod) => ({
    default: mod.OrganizationSetupView,
  })),
);
const PendingInvitationView = lazy(() =>
  import('views/PendingInvitationView').then((mod) => ({ default: mod.PendingInvitationView })),
);
const DateAssignmentDialog = lazy(() =>
  import('ui/DateAssignmentDialog').then((mod) => ({ default: mod.DateAssignmentDialog })),
);
const PriorityAssignmentDialog = lazy(() =>
  import('ui/PriorityAssignmentDialog').then((mod) => ({
    default: mod.PriorityAssignmentDialog,
  })),
);
const IssueAssignmentDialog = lazy(() =>
  import('ui/IssueAssignmentDialog').then((mod) => ({ default: mod.IssueAssignmentDialog })),
);
const InfoPanel = lazy(() => import('ui/InfoPanel').then((mod) => ({ default: mod.InfoPanel })));

const ROUTES_WITH_OWN_HEADER: RouteNames[] = [RouteNames.HOME, RouteNames.PROJECTS];
const CQ_ADMIN_ROUTES: RouteNames[] = [
  RouteNames.CQ_ADMIN,
  RouteNames.CQ_ADMIN_ORGANIZATIONS,
  RouteNames.CQ_ADMIN_USER_STATUS,
  RouteNames.CQ_ADMIN_USER_STATUS_INVITATIONS,
  RouteNames.CQ_ADMIN_USERS,
  RouteNames.CQ_ADMIN_USAGE_LIMITS,
  RouteNames.CQ_ADMIN_CQ_ADMINS,
  RouteNames.CQ_ADMIN_TROUBLESHOOTING,
  RouteNames.CQ_ADMIN_AUDIT_LOGS,
  RouteNames.CQ_ADMIN_SESSION_SHARE_TOKENS,
  RouteNames.CQ_ADMIN_ORG_DETAILS,
  RouteNames.CQ_ADMIN_ORG_MEMBERSHIP,
  RouteNames.CQ_ADMIN_ORG_SESSIONS,
];

const viewMap: Record<RouteNames, React.ReactNode> = {
  home: <HomeView />,
  projects: <ProjectsView />,
  importcsv: <ImportCSVView />,
  importlist: <ImportListView />,
  importspreadsheet: <ImportBlankSpreadsheetView />,
  importimages: <ImportImageView />,
  importpdf: <ImportPDFView />,
  'onboarding-project-info': <OnboardingView />,
  'onboarding-source-selection': <OnboardingView />,
  'onboarding-data-upload': <OnboardingView />,
  'session-home': <ActiveSessionView />,
  'session-search': <ActiveSessionView />,
  'session-home-source': <ActiveSessionView />,
  'session-queue': <ActiveSessionView />,
  'session-queue-legacy': <ActiveSessionView />,
  'session-step': <ActiveSessionView />,
  'session-dashboard': <ActiveSessionView />,
  'session-milestones': <ActiveSessionView />,
  'session-notes': <ActiveSessionView />,
  'session-find': <ActiveSessionView />,
  'session-library': <ActiveSessionView />,
  'session-library-legacy': <ActiveSessionView />,
  'session-assets': <ActiveSessionView />,
  'session-whole': <ActiveSessionView />,
  card: <ActiveSessionView />,
  // Legacy routes (will redirect but handle them during the brief moment before redirect)
  'session-legacy': <ActiveSessionView />,
  'session-dashboard-legacy': <ActiveSessionView />,
  'session-milestones-legacy': <ActiveSessionView />,
  'session-notes-legacy': <ActiveSessionView />,
  'card-legacy': <ActiveSessionView />,
  // Organization and Admin routes
  organizationAdmin: <OrganizationAdmin />,
  cqAdmin: <CQAdmin />,
  cqAdminOrganizations: <CQAdmin />,
  cqAdminUserStatus: <CQAdmin />,
  cqAdminUserStatusInvitations: <CQAdmin />,
  cqAdminUsers: <CQAdmin />,
  cqAdminUsageLimits: <CQAdmin />,
  cqAdminCQAdmins: <CQAdmin />,
  cqAdminTroubleshooting: <CQAdmin />,
  cqAdminAuditLogs: <CQAdmin />,
  cqAdminSessionShareTokens: <CQAdmin />,
  cqAdminOrgDetails: <OrganizationDetailsView />,
  cqAdminOrgMembership: <OrganizationDetailsView />,
  cqAdminOrgSessions: <OrganizationDetailsView />,
  sessionShare: <SessionShareView />,
  claimInvitation: <ClaimInvitationView />,
  approveOwnershipTransfer: <ApproveOwnershipTransferView />,
  tour: <TourView />,
};

const RoutedViews = () => (
  <Suspense fallback={<BrandedLoading debugSource="route-loading" />}>
    <RouterView viewMap={viewMap} />
  </Suspense>
);

const LazyAppOverlays = observer(() => {
  const { uiCardDateStore, uiCardPriorityStore, uiCardIssueStore, uiInfoPanelStore } = useStore();

  if (
    !uiCardDateStore.isDialogOpen &&
    !uiCardPriorityStore.isDialogOpen &&
    !uiCardIssueStore.isDialogOpen &&
    !uiInfoPanelStore.isOpen
  ) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      {uiCardDateStore.isDialogOpen && <DateAssignmentDialog />}
      {uiCardPriorityStore.isDialogOpen && <PriorityAssignmentDialog />}
      {uiCardIssueStore.isDialogOpen && <IssueAssignmentDialog />}
      {uiInfoPanelStore.isOpen && <InfoPanel />}
    </Suspense>
  );
});

export const App = Sentry.withProfiler(
  observer(() => {
    const {
      uiThemeStore: { darkMode, useSerifFont },
      authStore: { user, userLoading, setSignInRedirect },
      routerStore,
    } = useStore();

    useEffect(() => {
      const currentRoute = routerStore.getCurrentRoute()?.name as RouteNames | undefined;
      // Allow unauthenticated access to HOME, SESSION_SHARE, and CLAIM_INVITATION routes
      // Also allow guest users to access SESSION route
      const allowedUnauthenticatedRoutes = [
        RouteNames.HOME,
        RouteNames.PROJECTS,
        RouteNames.SESSION_SHARE,
        RouteNames.CLAIM_INVITATION,
        RouteNames.APPROVE_OWNERSHIP_TRANSFER,
      ];

      const isSessionRoute =
        currentRoute?.startsWith('session-') || currentRoute === RouteNames.CARD;

      if (
        userLoading === false &&
        !user &&
        currentRoute &&
        !isSessionRoute &&
        !allowedUnauthenticatedRoutes.includes(currentRoute)
      ) {
        setSignInRedirect(routerStore.routerState);
        routerStore.goTo(RouteNames.HOME);
      }

      // Don't redirect guest users away from SESSION route
      // Guest users are authenticated and should be able to access their session
    }, [user, userLoading, routerStore, setSignInRedirect]);

    return (
      <Sentry.ErrorBoundary
        fallback={
          <p>
            An error occurred, and our team has been notified. Please refresh the page to continue.
          </p>
        }
      >
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={getTheme(darkMode, useSerifFont)}>
            <ConfirmProvider>
              <CustomKbar>
                <AppView />
              </CustomKbar>
            </ConfirmProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </Sentry.ErrorBoundary>
    );
  }),
);

const useStyles = makeStyles((theme) => ({
  root: {
    transition: theme.transitions.create('background-color'),
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgb(10, 10, 10)' : theme.palette.background.default,
    color: theme.palette.text.primary,
    display: 'flex',
    flexDirection: 'column',
    width: '100vw',
    height: '100vh',
    overflow: 'hidden',
  },
  mainView: {
    flex: 1,
    overflow: 'auto',
  },
  '@global': {
    '.firebase-emulator-warning': {
      display: 'none',
    },
    a: {
      color: theme.palette.primary.light,
    },
  },
}));

const AppView = observer(() => {
  const styles = useStyles();
  const {
    authStore: { user },
    routerStore,
  } = useStore();

  // Check if we're on a route that should always show RouterView (even during loading)
  const currentRouteForRouter = routerStore.getCurrentRoute()?.name as RouteNames | undefined;
  const shouldBypassProjectSearchLoader = shouldBypassAppLoaderForProjectSearch({
    pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
    routeName: currentRouteForRouter,
  });
  const alwaysShowRouterRoutes = [
    RouteNames.SESSION_SHARE,
    RouteNames.CLAIM_INVITATION,
    RouteNames.APPROVE_OWNERSHIP_TRANSFER,
  ];
  const shouldAlwaysShowRouter =
    shouldBypassProjectSearchLoader ||
    (currentRouteForRouter && alwaysShowRouterRoutes.includes(currentRouteForRouter));

  useAppStartupTracingFinish(user, currentRouteForRouter, Boolean(shouldAlwaysShowRouter));

  // Redirect old /session/... URLs to /project/... URLs
  // Use ref to track last processed route to prevent infinite loops
  const lastProcessedLegacyRouteRef = useRef<string | null>(null);

  useEffect(() => {
    // Use MobX reaction to watch for route changes without causing dependency issues
    const dispose = reaction(
      () => ({
        routeName: routerStore.getCurrentRoute()?.name,
        sessionId: routerStore.routerState?.params?.sessionId,
        cardId: routerStore.routerState?.params?.cardId,
      }),
      ({ routeName, sessionId, cardId }) => {
        // Create a stable identifier for this route state
        const routeKey = routeName ? `${routeName}-${sessionId || ''}-${cardId || ''}` : null;

        // Skip if we've already processed this exact route state
        if (routeKey === lastProcessedLegacyRouteRef.current) {
          return;
        }

        // Redirect /project/:sessionId to its default surface (canonical URL).
        // Whole View flag on ⇒ land on the God's-eye board; otherwise SEARCH.
        if (routeName === RouteNames.SESSION_HOME && sessionId) {
          lastProcessedLegacyRouteRef.current = routeKey;
          routerStore.goTo(
            isWholeViewEnabled() ? RouteNames.SESSION_WHOLE : RouteNames.SESSION_SEARCH,
            { params: { sessionId } },
          );
          return;
        }

        // Only redirect if we're on a legacy route (check route name, not pathname)
        if (
          routeName === RouteNames.SESSION_LEGACY ||
          routeName === RouteNames.SESSION_DASHBOARD_LEGACY ||
          routeName === RouteNames.SESSION_MILESTONES_LEGACY ||
          routeName === RouteNames.SESSION_NOTES_LEGACY ||
          routeName === RouteNames.CARD_LEGACY
        ) {
          if (sessionId) {
            // Mark this route as processed before redirecting
            lastProcessedLegacyRouteRef.current = routeKey;

            // Determine the target route based on current route name
            let targetRoute: RouteNames;
            if (routeName === RouteNames.SESSION_DASHBOARD_LEGACY) {
              targetRoute = RouteNames.SESSION_DASHBOARD;
            } else if (routeName === RouteNames.SESSION_MILESTONES_LEGACY) {
              targetRoute = RouteNames.SESSION_MILESTONES;
            } else if (routeName === RouteNames.SESSION_NOTES_LEGACY) {
              targetRoute = RouteNames.SESSION_NOTES;
            } else if (routeName === RouteNames.CARD_LEGACY && cardId) {
              targetRoute = RouteNames.CARD;
            } else {
              targetRoute = RouteNames.SESSION_SEARCH;
            }

            // Build redirect params
            const redirectParams: { sessionId: string; cardId?: string } = { sessionId };
            if (cardId) {
              redirectParams.cardId = cardId;
            }

            // Redirect to new URL
            routerStore.goTo(targetRoute, {
              params: redirectParams,
            });
          }
        } else {
          // Reset the ref when we're not on a legacy route
          lastProcessedLegacyRouteRef.current = null;
        }
      },
    );

    return () => dispose();
  }, [routerStore]);

  // isSessionRoute is computed but currently not used - keeping for potential future use
  // const isSessionRoute =
  //   currentRoute?.name === RouteNames.SESSION_HOME ||
  //   currentRoute?.name === RouteNames.SESSION_HOME_SOURCE ||
  //   currentRoute?.name === RouteNames.SESSION_QUEUE ||
  //   currentRoute?.name === RouteNames.SESSION_DASHBOARD ||
  //   currentRoute?.name === RouteNames.SESSION_MILESTONES ||
  //   currentRoute?.name === RouteNames.SESSION_NOTES ||
  //   currentRoute?.name === RouteNames.SESSION_FIND ||
  //   currentRoute?.name === RouteNames.SESSION_LIBRARY ||
  //   currentRoute?.name === RouteNames.CARD ||
  //   // Legacy routes (will redirect but handle them during the brief moment before redirect)
  //   currentRoute?.name === RouteNames.SESSION_LEGACY ||
  //   currentRoute?.name === RouteNames.SESSION_DASHBOARD_LEGACY ||
  //   currentRoute?.name === RouteNames.SESSION_MILESTONES_LEGACY ||
  //   currentRoute?.name === RouteNames.SESSION_NOTES_LEGACY ||
  //   currentRoute?.name === RouteNames.CARD_LEGACY;

  // Hide ApplicationBar on routes with their own header (ApplicationBar uses useProjectTab + sessionsStore which require an active session)
  const currentRoute = routerStore.getCurrentRoute()?.name as RouteNames | undefined;
  const shouldHideAppBar =
    currentRoute &&
    (ROUTES_WITH_OWN_HEADER.includes(currentRoute) || CQ_ADMIN_ROUTES.includes(currentRoute));

  return (
    <>
      <main className={styles.root}>
        {!shouldHideAppBar && <ApplicationBar />}
        <div className={styles.mainView}>
          {shouldAlwaysShowRouter ? (
            // Always show RouterView for routes that need to work without authentication
            <RoutedViews />
          ) : user === undefined ||
            (user !== null && user.canUseApp === undefined) ||
            (user && user.hasOrganization === undefined) ? (
            <BrandedLoading debugSource="app-auth-gate" />
          ) : user && !user.canUseApp && !user.isGuest ? (
            <Suspense fallback={<BrandedLoading debugSource="route-loading" />}>
              <AccountAccessBlocked />
            </Suspense>
          ) : user && user.hasPendingInvitation ? (
            <Suspense fallback={<BrandedLoading debugSource="route-loading" />}>
              <PendingInvitationView />
            </Suspense>
          ) : user && !user.hasOrganization && !user.isGuest ? (
            <Suspense fallback={<BrandedLoading debugSource="route-loading" />}>
              <OrganizationSetupView />
            </Suspense>
          ) : (
            <RoutedViews />
          )}
        </div>
      </main>
      <AppSnackbar />
      <LazyAppOverlays />
    </>
  );
});
